import React, { useEffect, useRef } from 'react';
import useSnapIndex from '../hooks/useSnapIndex';
import './SnapRow.css';

// A horizontal row of cards that scrolls natively and snaps to each card.
//
// This is deliberately NOT the same mechanism as Carousel. Carousel is a
// paginated slider that moves a track with a CSS transform and owns a drag
// gesture - right for "3 cards at a time, arrows step by one". SnapRow is the
// pattern a phone user expects from a photo strip or a row of feature cards:
// the browser owns the scrolling (momentum, rubber-banding, accessibility), CSS
// scroll-snap lands each card cleanly, and cards are fixed-width so the next
// one peeks in from the edge as an invitation to swipe. The only JavaScript is
// reading the scroll position to know which card is current, and nudging the
// scroll when an arrow is clicked. One mechanism per component; neither
// borrows from the other.
//
// `itemWidth` is a CSS length, or {base, md, lg} for per-breakpoint widths.
// On a wide screen where every card fits, the row simply does not scroll and
// the arrows hide themselves.
//
// `wrap` ('md' or 'lg') turns the row into a wrapping grid from that
// breakpoint up: a phone swipes through the cards, a laptop sees them all at
// once. One list either way - CSS changes how it flows, so there is never a
// second copy of the cards in the page for the other screen size.
//
// `bleed` (a CSS length, e.g. '20px') lets the scrolling row run to the screen
// edge like a photo roll, with the first card still lined up with the text
// above - pass the host section's side padding. The row owns this itself so
// no page has to restyle its track.

function toVars(itemWidth) {
  if (typeof itemWidth === 'string') return { '--snap-w': itemWidth };
  const v = {};
  if (itemWidth.base) v['--snap-w'] = itemWidth.base;
  if (itemWidth.md) v['--snap-w-md'] = itemWidth.md;
  if (itemWidth.lg) v['--snap-w-lg'] = itemWidth.lg;
  return v;
}

export default function SnapRow({
  items = [],
  renderItem,
  itemWidth = '82%',
  gap = 12,
  ariaLabel = 'Items',
  className = '',
  arrows = true,
  counter = false,
  wrap,
  bleed,
  onIndexChange,
}) {
  const trackRef = useRef(null);
  const { index, canScroll } = useSnapIndex(trackRef, [items.length]);

  useEffect(() => { if (onIndexChange) onIndexChange(index); }, [index, onIndexChange]);

  const scrollToIndex = (i) => {
    const track = trackRef.current;
    if (!track) return;
    const target = track.children[Math.max(0, Math.min(items.length - 1, i))];
    if (!target) return;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({ left: target.offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
  };

  return (
    <div className={`snap${wrap ? ` snap--wrap-${wrap}` : ''} ${className}`.trim()} style={{ ...toVars(itemWidth), ...(bleed ? { '--snap-bleed': bleed } : {}) }} role="region" aria-label={ariaLabel}>
      <ul className="snap-track" ref={trackRef} style={{ gap: `${gap}px` }}>
        {items.map((it, i) => (
          <li className="snap-item" key={i} aria-label={`${i + 1} of ${items.length}`}>
            {renderItem(it, i)}
          </li>
        ))}
      </ul>
      {counter && canScroll && (
        <div className="snap-counter" aria-live="polite">{index + 1} / {items.length}</div>
      )}
      {arrows && canScroll && (
        <>
          <button type="button" className="snap-arrow snap-arrow--prev" onClick={() => scrollToIndex(index - 1)} disabled={index === 0} aria-label="Previous">‹</button>
          <button type="button" className="snap-arrow snap-arrow--next" onClick={() => scrollToIndex(index + 1)} disabled={index >= items.length - 1} aria-label="Next">›</button>
        </>
      )}
    </div>
  );
}
